import configuration from "@/configuration";
import { useAuth0 } from "@auth0/auth0-react";
import { ArrowRightStartOnRectangleIcon, Bars3Icon, EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import {
    alpha,
    Avatar,
    Box,
    Button,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { MouseEvent, useState } from "react";
import { Link } from "react-router-dom";

const pages = [
    {
        title: "Website",
        href: "/",
    },
    {
        title: "BIMQ",
        href: "https://server.bim-q.de/",
    },
    {
        title: "Google Analytics",
        href: "https://analytics.google.com/",
    },
];

interface TopbarProps {
    onSidebarOpen: () => void;
}

const Topbar = ({ onSidebarOpen }: TopbarProps) => {
    const theme = useTheme();
    const [menuAnchorElement, setMenuAnchorElement] = useState<HTMLElement | null>(null);
    const [userAnchorElement, setUserAnchorElement] = useState<HTMLElement | null>(null);
    const menuOpen = Boolean(menuAnchorElement);
    const userOpen = Boolean(userAnchorElement);

    const { user, logout } = useAuth0();

    const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => setMenuAnchorElement(event.currentTarget);
    const handleMenuClose = () => setMenuAnchorElement(null);
    const handleUserClick = (event: MouseEvent<HTMLButtonElement>) => setUserAnchorElement(event.currentTarget);
    const handleUserClose = () => setUserAnchorElement(null);

    return (
        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} width={1} maxHeight={46}>
            <Box display={"flex"} title={"IDS Platform - Administration"} width={{ xs: 100, md: 120 }}>
                <Link to={"/admin"}>
                    <Box
                        component={"img"}
                        src={`${configuration.assets}content/logo-bimids.png`}
                        height={1}
                        maxHeight={48}
                        // width={1}
                    />
                </Link>
            </Box>
            <Box sx={{ display: "flex", ml: { xs: "auto" } }}>
                <Box sx={{ display: { xs: "none", md: "flex" } }} alignItems={"center"}>
                    {pages.map((item, i) => (
                        <Box marginLeft={3} key={i}>
                            <Link to={item.href} target="_blank" className="hover:underline">
                                <Typography color="text.primary">{item.title}</Typography>
                            </Link>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ display: { xs: "flex", md: "none" } }} alignItems={"center"}>
                    <Tooltip title="Menu">
                        <IconButton onClick={(e) => handleMenuClick(e)} size="small">
                            <EllipsisHorizontalIcon />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={menuAnchorElement}
                        open={menuOpen}
                        onClose={handleMenuClose}
                        onClick={handleMenuClose}
                        slotProps={{
                            paper: {
                                elevation: 3,
                                sx: {
                                    overflow: "visible",
                                    mt: 1.5,
                                    "&:before": {
                                        content: '""',
                                        display: "block",
                                        position: "absolute",
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: "background.paper",
                                        transform: "translateY(-50%) rotate(45deg)",
                                        zIndex: 0,
                                    },
                                },
                            },
                        }}
                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                    >
                        {pages.map((item, i) => (
                            <MenuItem key={i}>
                                <Link to={item.href}>
                                    <Typography color="text.primary">{item.title}</Typography>
                                </Link>
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>
            </Box>
            <Box display={"flex"}>
                <Box sx={{ display: "flex" }} marginLeft={2}>
                    <IconButton size="small" onClick={handleUserClick}>
                        <Avatar alt={user?.name} src={user?.picture} />
                    </IconButton>
                </Box>

                <Menu
                    id="menu-appbar"
                    anchorEl={userAnchorElement}
                    open={userOpen}
                    onClose={handleUserClose}
                    slotProps={{
                        paper: {
                            elevation: 3,
                            sx: {
                                overflow: "visible",
                                mt: 1.5,
                                "&:before": {
                                    content: '""',
                                    display: "block",
                                    position: "absolute",
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: "background.paper",
                                    transform: "translateY(-50%) rotate(45deg)",
                                    zIndex: 0,
                                },
                                "& .MuiAvatar-root": {
                                    width: 32,
                                    height: 32,
                                    ml: -0.5,
                                    mr: 1,
                                },
                            },
                        },
                    }}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                    <MenuItem onClick={handleUserClose}>
                        <ListItemIcon>
                            <Avatar alt={user?.name} src={user?.picture} />
                        </ListItemIcon>
                        {user!.name}
                    </MenuItem>
                    <Divider />
                    <MenuItem
                        onClick={() =>
                            logout({
                                logoutParams: {
                                    returnTo: configuration.auth.logoutTo,
                                },
                            })
                        }
                    >
                        <ListItemIcon>
                            <ArrowRightStartOnRectangleIcon className="w-6 h-6" />
                        </ListItemIcon>
                        Logout
                    </MenuItem>
                </Menu>
            </Box>
            <Box display={"flex"}>
                <Box sx={{ display: { xs: "block", md: "none" } }} marginLeft={2}>
                    <Button
                        onClick={() => onSidebarOpen()}
                        aria-label="Menu"
                        variant={"outlined"}
                        sx={{
                            borderRadius: 2,
                            minWidth: "auto",
                            padding: 1,
                            borderColor: alpha(theme.palette.divider, 0.2),
                        }}
                    >
                        <Bars3Icon />
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default Topbar;
